import { useState } from "react";
import { Tag, MapPin, Plus, Pencil, Trash2 } from "lucide-react";
import { assetApi } from "../api";
import { useAsyncData, useVoidMutation } from "../hooks";
import {
  AssetError, AssetLoading, BackBtn, Btn, Card, CardHeader, PageHeader,
} from "../components/AssetUI";
import type { AssetCategory, AssetLocation } from "../types";

type Mode = "categories" | "locations";

export default function AssetSetupPage() {
  const [mode, setMode] = useState<Mode>("categories");
  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Asset Management"
        title="Asset Setup"
        actions={<BackBtn to="/assets" label="Dashboard" />}
      />
      <div className="flex gap-1 rounded-2xl border bg-white p-1.5 shadow-sm">
        {(["categories", "locations"] as Mode[]).map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)}
            className={`flex-1 capitalize rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${mode === m ? "bg-indigo-600 text-white shadow-sm" : "text-slate-500 hover:bg-slate-50"}`}>
            {m === "categories" ? "Categories" : "Locations"}
          </button>
        ))}
      </div>
      {mode === "categories" ? <CategoriesPanel /> : <LocationsPanel />}
    </div>
  );
}

// ── Categories ──────────────────────────────────────────────────────────────

type CatForm = { code: string; name: string; description: string; depreciationRate: string; usefulLifeYears: string; assignable: boolean };
const emptyCatForm = (): CatForm => ({ code: "", name: "", description: "", depreciationRate: "", usefulLifeYears: "", assignable: true });

function CategoriesPanel() {
  const { data, loading, error, reload } = useAsyncData(() => assetApi.listCategories(), []);
  const [form, setForm]   = useState<CatForm>(emptyCatForm());
  const [editing, setEditing] = useState<AssetCategory | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  function startEdit(c: AssetCategory) {
    setEditing(c);
    setForm({
      code: c.code ?? "",
      name: c.name,
      description: c.description ?? "",
      depreciationRate: c.depreciationRate != null ? String(c.depreciationRate) : "",
      usefulLifeYears: c.usefulLifeYears != null ? String(c.usefulLifeYears) : "",
      assignable: c.assignable !== false,  // default true
    });
    setShowAdd(false);
  }
  function cancelForm() { setEditing(null); setShowAdd(false); setForm(emptyCatForm()); }

  const saveMut = useVoidMutation(async () => {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      depreciationRate: form.depreciationRate ? Number(form.depreciationRate) : undefined,
      usefulLifeYears: form.usefulLifeYears ? Number(form.usefulLifeYears) : undefined,
      assignable: form.assignable,
    };
    if (editing) {
      await assetApi.updateCategory(editing.id, payload);
    } else {
      await assetApi.createCategory(payload as Parameters<typeof assetApi.createCategory>[0]);
    }
    reload(); cancelForm();
  });

  if (loading) return <AssetLoading label="Loading categories…" />;
  if (error)   return <AssetError message={error} onRetry={reload} />;

  return (
    <Card noPad>
      <CardHeader title="Asset Categories" subtitle={`${data?.length ?? 0} categories configured`}
        action={<Btn variant="primary" size="sm" icon={Plus} onClick={() => { setShowAdd(true); setEditing(null); setForm(emptyCatForm()); }}>Add Category</Btn>}
      />
      {showAdd && <CatFormRow form={form} setForm={setForm} onSave={() => saveMut.mutate()} onCancel={cancelForm} loading={saveMut.loading} error={saveMut.error} />}
      <div className="divide-y">
        {data?.map((cat) => (
          <>
            <div key={cat.id} className="flex items-center gap-4 px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50"><Tag className="h-4 w-4 text-indigo-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800">{cat.name}</p>
                <p className="text-xs text-slate-400 font-mono">{cat.code}{cat.description ? ` · ${cat.description}` : ""}</p>
              </div>
              {cat.depreciationRate != null && <span className="text-xs font-semibold text-amber-600">{cat.depreciationRate}% dep./yr</span>}
              {cat.usefulLifeYears != null && <span className="text-xs text-slate-400">{cat.usefulLifeYears} yr life</span>}
              {cat.assetCount != null && <span className="text-xs text-slate-400">{cat.assetCount} assets</span>}
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cat.assignable !== false ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-400"}`}>
                {cat.assignable !== false ? "Assignable" : "Non-assignable"}
              </span>
              <div className="flex gap-1.5">
                <Btn variant="ghost" size="sm" icon={Pencil} onClick={() => startEdit(cat)} />
                <Btn variant="ghost" size="sm" icon={Trash2} className="text-red-400 hover:text-red-600" />
              </div>
            </div>
            {editing?.id === cat.id && <CatFormRow form={form} setForm={setForm} onSave={() => saveMut.mutate()} onCancel={cancelForm} loading={saveMut.loading} error={saveMut.error} />}
          </>
        ))}
      </div>
    </Card>
  );
}

function CatFormRow({ form, setForm, onSave, onCancel, loading, error }: {
  form: CatForm; setForm: (f: CatForm) => void;
  onSave: () => void; onCancel: () => void;
  loading: boolean; error?: string | null;
}) {
  const u = (k: keyof CatForm, v: string) => setForm({ ...form, [k]: v });
  return (
    <div className="border-b bg-slate-50 px-5 py-4 space-y-3">
      <div className="flex flex-wrap gap-3">
        <input className="w-32 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Code *" value={form.code} onChange={(e) => u("code", e.target.value)} />
        <input className="flex-1 min-w-40 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Category name *" value={form.name} onChange={(e) => u("name", e.target.value)} />
        <input className="flex-1 min-w-48 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Description" value={form.description} onChange={(e) => u("description", e.target.value)} />
        <input type="number" className="w-36 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Dep. % / yr" value={form.depreciationRate} onChange={(e) => u("depreciationRate", e.target.value)} />
        <input type="number" className="w-28 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Useful yrs" value={form.usefulLifeYears} onChange={(e) => u("usefulLifeYears", e.target.value)} />
        <label className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={form.assignable} onChange={(e) => setForm({ ...form, assignable: e.target.checked })} className="accent-indigo-600" />
          <span className="text-slate-600">Assignable to employees</span>
        </label>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Btn variant="primary" size="sm" loading={loading} loadingText="Saving…" onClick={onSave}>Save</Btn>
        <Btn variant="secondary" size="sm" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}

// ── Locations ───────────────────────────────────────────────────────────────

type LocForm = { code: string; name: string; building: string; floor: string; room: string; description: string };
const emptyLocForm = (): LocForm => ({ code: "", name: "", building: "", floor: "", room: "", description: "" });

function LocationsPanel() {
  const { data, loading, error, reload } = useAsyncData(() => assetApi.listLocations(), []);
  const [form, setForm]   = useState<LocForm>(emptyLocForm());
  const [editing, setEditing] = useState<AssetLocation | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  function startEdit(l: AssetLocation) {
    setEditing(l);
    setForm({
      code: l.code ?? "",
      name: l.name,
      building: l.building ?? "",
      floor: l.floor ?? "",
      room: l.room ?? "",
      description: l.description ?? "",
    });
    setShowAdd(false);
  }
  function cancelForm() { setEditing(null); setShowAdd(false); setForm(emptyLocForm()); }

  const saveMut = useVoidMutation(async () => {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      building: form.building.trim() || undefined,
      floor: form.floor.trim() || undefined,
      room: form.room.trim() || undefined,
      description: form.description.trim() || undefined,
    };
    if (editing) {
      await assetApi.updateLocation(editing.id, payload);
    } else {
      await assetApi.createLocation(payload as Parameters<typeof assetApi.createLocation>[0]);
    }
    reload(); cancelForm();
  });

  if (loading) return <AssetLoading label="Loading locations…" />;
  if (error)   return <AssetError message={error} onRetry={reload} />;

  return (
    <Card noPad>
      <CardHeader title="Asset Locations" subtitle={`${data?.length ?? 0} locations configured`}
        action={<Btn variant="primary" size="sm" icon={Plus} onClick={() => { setShowAdd(true); setEditing(null); setForm(emptyLocForm()); }}>Add Location</Btn>}
      />
      {showAdd && <LocFormRow form={form} setForm={setForm} onSave={() => saveMut.mutate()} onCancel={cancelForm} loading={saveMut.loading} error={saveMut.error} />}
      <div className="divide-y">
        {data?.map((loc) => (
          <>
            <div key={loc.id} className="flex items-center gap-4 px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50"><MapPin className="h-4 w-4 text-emerald-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800">{loc.name}</p>
                <p className="text-xs text-slate-400 font-mono">{loc.code}{[loc.building, loc.floor, loc.room].filter(Boolean).length ? ` · ${[loc.building, loc.floor, loc.room].filter(Boolean).join(", ")}` : ""}</p>
              </div>
              {loc.assetCount != null && <span className="text-xs text-slate-400">{loc.assetCount} assets</span>}
              <div className="flex gap-1.5">
                <Btn variant="ghost" size="sm" icon={Pencil} onClick={() => startEdit(loc)} />
                <Btn variant="ghost" size="sm" icon={Trash2} className="text-red-400 hover:text-red-600" />
              </div>
            </div>
            {editing?.id === loc.id && <LocFormRow form={form} setForm={setForm} onSave={() => saveMut.mutate()} onCancel={cancelForm} loading={saveMut.loading} error={saveMut.error} />}
          </>
        ))}
      </div>
    </Card>
  );
}

function LocFormRow({ form, setForm, onSave, onCancel, loading, error }: {
  form: LocForm; setForm: (f: LocForm) => void;
  onSave: () => void; onCancel: () => void;
  loading: boolean; error?: string | null;
}) {
  const u = (k: keyof LocForm, v: string) => setForm({ ...form, [k]: v });
  return (
    <div className="border-b bg-slate-50 px-5 py-4 space-y-3">
      <div className="flex flex-wrap gap-3">
        <input className="w-28 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Code *" value={form.code} onChange={(e) => u("code", e.target.value)} />
        <input className="flex-1 min-w-40 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Location name *" value={form.name} onChange={(e) => u("name", e.target.value)} />
        <input className="flex-1 min-w-32 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Building" value={form.building} onChange={(e) => u("building", e.target.value)} />
        <input className="w-24 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Floor" value={form.floor} onChange={(e) => u("floor", e.target.value)} />
        <input className="w-24 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Room" value={form.room} onChange={(e) => u("room", e.target.value)} />
        <input className="flex-1 min-w-40 rounded-xl border px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder="Description" value={form.description} onChange={(e) => u("description", e.target.value)} />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Btn variant="primary" size="sm" loading={loading} loadingText="Saving…" onClick={onSave}>Save</Btn>
        <Btn variant="secondary" size="sm" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}
